/**
 * Remove Account Step Component
 * Uses same pattern as CreateAccount with StepContent
 */

import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '../../../../services/serviceTypes';
import type { StepFormMessage } from '../../data-layer/types';
import { setSavedStepData } from '../../data-store/copilotSlice';
import useFormValidation from '../../hooks/useFormValidation';
import { FORM_RESPONSE_TEXTS } from '../../services/responseTextService';
import StepValidationService from '../../services/stepValidationService';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import ExpandableForm from '../ExpandableForm/ExpandableForm';
import ResponseWithForm from '../ResponseWithForm/ResponseWithForm';
import StepRenderer from '../StepRenderer/StepRenderer';

interface RemoveAccountStepProps {
  message: StepFormMessage;
  onFormSubmit: (
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ) => void;
  onSkip: (currentStep: string, nextStep: string) => void;
}

/**
 * RemoveAccountStep component for handling account removal configuration
 */
function RemoveAccountStep({
  message,
  onSkip,
  onFormSubmit,
}: RemoveAccountStepProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const dispatch = useDispatch();

  // Form validation hook
  const { validateForm } = useFormValidation();

  // Get persisted form data from Redux
  const persistedFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData.RemoveAccount
  );

  // Check if in edit mode
  const isInEditMode = useSelector(
    (state: RootState) => state.copilot.ui.mode === 'edit'
  );

  // Check if step is completed
  const isCompletedCheck = useSelector(
    (state: RootState) => state.copilot.navigation.completedSteps ?? {}
  );

  const skippedSteps = useSelector(
    (state: RootState) => state.copilot.navigation.skippedSteps ?? []
  );

  const btnlabel = isCompletedCheck.includes(message.stepName ?? '');
  const isStepCompletedOrSkipped =
    isCompletedCheck.includes('RemoveAccount') ||
    skippedSteps.includes('RemoveAccount');

  const validateAndContinue = useCallback(async () => {
    setValidationError(null);

    // Use centralized validation service
    const validation = await StepValidationService.validateStep(
      'RemoveAccount',
      persistedFormData,
      validateForm
    );

    if (validation.isValid) {
      // Save to savedStepData on successful validation
      dispatch(
        setSavedStepData({
          stepName: 'RemoveAccount',
          formData: persistedFormData,
        })
      );

      onFormSubmit('RemoveAccount', persistedFormData, validation.isValid);
    } else {
      setValidationError(validation.errors[0] || 'Validation failed');
    }

    // In edit mode, don't collapse form after successful submission
    return validation.isValid && !isInEditMode;
  }, [dispatch, persistedFormData, onFormSubmit, isInEditMode, validateForm]);

  const handleSkipStep = () => {
    onSkip('RemoveAccount', 'AddAccess');
  };

  return (
    <ResponseWithForm
      responseText={
        !isInEditMode
          ? FORM_RESPONSE_TEXTS.REMOVE_ACCOUNT
          : FORM_RESPONSE_TEXTS.REMOVE_ACCOUNT_EDIT
      }>
      <ExpandableForm
        stepName={message.stepName ?? 'RemoveAccount'}
        heading={message.heading}
        buttonLabel={btnlabel ? 'Update' : message.buttonLabel}
        buttonkind='outlined'
        onSave={validateAndContinue}
        onSecondory={handleSkipStep}
        secondryButtonLabel={isInEditMode ? undefined : 'Skip'}
        isSecondaryDisabled={isStepCompletedOrSkipped}>
        <StepRenderer stepName='RemoveAccount' />
        {validationError && <ErrorBanner message={validationError} />}
      </ExpandableForm>
    </ResponseWithForm>
  );
}

export default RemoveAccountStep;
